import axios from 'axios';
import { deviceHeaders } from '../lib/device';   // r7.4 device-bound QR

/**
 * ════════════════════════════════════════════════════════════════
 * API client — pinned to the gateway at /api
 *
 *  - Sends Authorization: Bearer <accessToken> on every request
 *  - On 401, transparently refreshes via /api/auth/refresh and retries
 *  - On refresh failure → broadcasts auth:unauthenticated event so the
 *    AuthContext can clear state and redirect to /login
 *  - Idempotency-Key auto-injected for non-GET/HEAD writes (uuid v4)
 * ════════════════════════════════════════════════════════════════
 */

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const REFRESH_PATH = '/auth/refresh';
// r7.6: a per-tenant build (Capacitor app, step 9) pins its tenant; the gateway otherwise resolves it from Host
const BUILD_TENANT = (import.meta.env.VITE_TENANT_ID || '').toLowerCase().trim() || null;

const TOKEN_KEY = 'snt:access_token';
const REFRESH_KEY = 'snt:refresh_token';

export const tokenStore = {
  get access() { return localStorage.getItem(TOKEN_KEY); },
  get refresh() { return localStorage.getItem(REFRESH_KEY); },
  set({ accessToken, refreshToken }) {
    if (accessToken) localStorage.setItem(TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

function uuidv4() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  // RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor ──────────────────────────────────────
api.interceptors.request.use((config) => {
  const token = tokenStore.access;
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // r7.4: device identity for device-bound QRs (server stores a hash; harmless on every other route)
  Object.assign(config.headers, deviceHeaders());
  // r7.6: tenant pin for per-tenant builds (the gateway honours X-Tenant-Id only for a known, non-suspended slug)
  if (BUILD_TENANT && !config.headers['X-Tenant-Id']) config.headers['X-Tenant-Id'] = BUILD_TENANT;
  // r7.5: analytics consent travels with the request so the edge can forward flag exposures for anonymous
  // devices too (signed-in users are checked server-side against auth.user_consent_log)
  try {
    const c = JSON.parse(localStorage.getItem('snt_consent_v1') || 'null');
    if (c && c.analytics) config.headers['X-SNT-Consent'] = 'analytics';
  } catch { /* no consent recorded yet */ }
  // Inject Idempotency-Key for write requests unless caller already supplied one
  const method = (config.method || 'get').toLowerCase();
  const isWrite = ['post', 'put', 'patch', 'delete'].includes(method);
  if (isWrite && !config.headers['Idempotency-Key']) {
    config.headers['Idempotency-Key'] = uuidv4();
  }
  return config;
});

// ─── Refresh token coordination ───────────────────────────────
let refreshInFlight = null;

async function refreshAccessToken() {
  if (refreshInFlight) return refreshInFlight;
  const refresh = tokenStore.refresh;
  if (!refresh) {
    return Promise.reject(new Error('NO_REFRESH_TOKEN'));
  }
  refreshInFlight = axios
    .post(`${API_BASE}${REFRESH_PATH}`, { refreshToken: refresh }, BUILD_TENANT ? { headers: { 'X-Tenant-Id': BUILD_TENANT } } : undefined)
    .then((r) => {
      const accessToken = r.data?.accessToken || r.data?.access_token;
      const newRefresh = r.data?.refreshToken || r.data?.refresh_token;
      if (!accessToken) throw new Error('REFRESH_NO_ACCESS_TOKEN');
      tokenStore.set({ accessToken, refreshToken: newRefresh });
      return accessToken;
    })
    .finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

// ─── Response interceptor ─────────────────────────────────────
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    if (!original) throw error;

    // Don't retry refresh-of-refresh
    if (original.url?.includes(REFRESH_PATH)) {
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent('auth:unauthenticated'));
      throw error;
    }

    const status = error.response?.status;
    if (status === 401 && !original._retry) {
      original._retry = true;
      try {
        const accessToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${accessToken}`;
        return api(original);
      } catch (refreshErr) {
        tokenStore.clear();
        window.dispatchEvent(new CustomEvent('auth:unauthenticated'));
        throw error;
      }
    }
    throw error;
  }
);

// ─── Endpoint helpers — keep the base small + typed ──────────
export const auth = {
  login: (body) => api.post('/auth/login', body).then(r => r.data),
  signup: (body) => api.post('/auth/signup', body).then(r => r.data),
  logout: () => api.post('/auth/logout').then(r => r.data),
  me: () => api.get('/users/me').then(r => r.data),
  // r7.10 (SIGN-004 / SIGN-005): the Google redirect's one-time handoff, and the password-reset pair the pages never had
  oauthHandoff: (code) => api.post('/auth/oauth/handoff', { code }).then(r => r.data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }).then(r => r.data),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, newPassword }).then(r => r.data),
};

export const events = {
  list: (params) => api.get('/events', { params }).then(r => r.data),
  get: (idOrSlug) => api.get(`/events/${idOrSlug}`).then(r => r.data),
  trending: (city) => api.get('/events/trending', { params: { city } }).then(r => r.data),
  shareKit: (id) => api.get(`/events/${id}/share-kit`).then(r => r.data),
};

export const bookings = {
  create: (body) => api.post('/bookings', body).then(r => r.data),
  mine: (params) => api.get('/bookings/mine', { params }).then(r => r.data),
  get: (id) => api.get(`/bookings/${id}`).then(r => r.data),
  cancel: (id, reason) => api.post(`/bookings/${id}/cancel`, { reason }).then(r => r.data),
  reschedule: (id, newShowId) => api.post(`/bookings/${id}/reschedule`, { new_show_id: newShowId }).then(r => r.data),
};

export const payments = {
  createOrder: (body) => api.post('/payments', body).then(r => r.data),
  verify: (id, body) => api.post(`/payments/${id}/verify`, body).then(r => r.data),
  mine: (params) => api.get('/me/payments', { params }).then(r => r.data),
  selfServeRefund: (paymentId, reason) => api.post(`/me/refunds/${paymentId}`, { reason }).then(r => r.data),
  gstInvoiceUrl: (paymentId) => `${API_BASE}/me/payments/${paymentId}/gst-invoice`,
};

export const ai = {
  // Non-streaming — useful when SSE is unavailable
  chatOnce: (body) => api.post('/ai/chat/once', body).then(r => r.data),
  // SSE streaming chat — caller wires the EventSource manually for control over UI rendering
  chatStreamUrl: () => `${API_BASE}/ai/chat`,
  recommendations: (params) => api.get('/ai/recommendations', { params }).then(r => r.data),
  search: (body) => api.post('/ai/search', body).then(r => r.data),
  classify: (body) => api.post('/ai/search/classify', body).then(r => r.data),
  generateCopy: (body) => api.post('/ai/copy/generate', body).then(r => r.data),
  myDrafts: () => api.get('/ai/copy/drafts').then(r => r.data),
  acceptDraft: (id) => api.post(`/ai/copy/drafts/${id}/accept`).then(r => r.data),
  pricingSuggest: (body) => api.post('/ai/pricing/suggest', body).then(r => r.data),
};

export const me = {
  wishlist: () => api.get('/me/wishlist').then(r => r.data),
  toggleWishlist: (eventId) => api.post(`/events/${eventId}/wishlist`).then(r => r.data),
  waitlist: () => api.get('/me/waitlist').then(r => r.data),
  joinWaitlist: (eventId, body) => api.post(`/events/${eventId}/waitlist`, body).then(r => r.data),
  referrals: () => api.get('/me/referrals').then(r => r.data),
  notifications: (params) => api.get('/me/notifications', { params }).then(r => r.data),
  preferences: () => api.get('/me/preferences').then(r => r.data),
  setPreferences: (body) => api.patch('/me/preferences', body).then(r => r.data),
  pushTokens: () => api.get('/me/push-tokens').then(r => r.data),
  registerPushToken: (body) => api.post('/me/push-tokens', body).then(r => r.data),
  assets: () => api.get('/me/assets').then(r => r.data),
};

export const organizer = {
  myEvents: () => api.get('/events/mine', { params: { limit: 100 } }).then(r => r.data),
  overview: (days = 30) => api.get('/bookings/organizer/overview', { params: { days } }).then(r => r.data),   // r7.10 (DEF-001)
  createEvent: (body) => api.post('/events', body).then(r => r.data),
  updateEvent: (id, body) => api.patch(`/events/${id}`, body).then(r => r.data),
  publishEvent: (id) => api.post(`/events/${id}/publish`).then(r => r.data),
  myVenues: () => api.get('/venues/mine').then(r => r.data),
  createVenue: (body) => api.post('/venues', body).then(r => r.data),
  claimVenue: (venueId, token) => api.post(`/admin/venues/${venueId}/claim`, { token }).then(r => r.data),
};

export const admin = {
  venues: (params) => api.get('/admin/venues', { params }).then(r => r.data),
  venueProspects: (params) => api.get('/admin/venues/prospects', { params }).then(r => r.data),
  importVenuesCSV: (csvText) =>
    api.post('/admin/venues/import', csvText, {
      headers: { 'Content-Type': 'text/csv' },
    }).then(r => r.data),
  updateVenueOnboarding: (id, status) =>
    api.patch(`/admin/venues/${id}/onboarding`, { onboarding_status: status }).then(r => r.data),
  inviteVenue: (id) => api.post(`/admin/venues/${id}/invite`).then(r => r.data),
  moderationQueue: (params) => api.get('/ai/moderate/queue', { params }).then(r => r.data),
  reviewModeration: (id, decision) =>
    api.post(`/ai/moderate/${id}/review`, { final_decision: decision }).then(r => r.data),
};

export default api;
